import { useMemo, useEffect } from "react";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useAppState } from "@/modules/app/useAppContext";
import { ExplorerHeader, ExplorerTabs, ExplorerSummary, ExplorerFilterTags } from "./components";
import { useOpportunitiesQuery, useOpportunityNamesQuery, useExplorerUsersQuery } from "./hooks";
import styles from "./styles.module.scss";

export const ExplorerContentOriginal = () => {
  const { userListWithUnassigned } = useExplorerUsersQuery();
  const { registerFilters } = useDatatableContext();
  const { searchParams } = useFiltersBySearchParams();
  const { enablePriorityTab } = useAppState();
  const priorityTabEnabled = enablePriorityTab ?? false;
  // /discover defaults to the Priority tab when the flag is on, matching
  // ExplorerTabs.
  const tab = searchParams.get("tab") || (priorityTabEnabled ? "priority" : "opportunities");
  const onPriorityTab = tab === "priority" && priorityTabEnabled;
  const needsOpportunitiesData = !onPriorityTab;

  // Register all Explorer filters with the DatatableContext
  useEffect(() => {
    registerFilters({
      // Date range filters
      start_date: { label: "Start Date", isArray: false },
      end_date: { label: "End Date", isArray: false },
      period: { label: "Period", isArray: false },
      // Sort filters
      sort: { label: "Sort", isArray: false },
      sort_order: { label: "Sort Order", isArray: false },
      // Filter dropdown filters
      effort: { label: "Effort", isArray: true },
      resource_name: { label: "Resource Name", isArray: true },
      opportunity_name: { label: "Opportunity Name", isArray: true },
      resource_type: { label: "Resource Type", isArray: true },
      owner: { label: "Owner", isArray: true },
      status: { label: "Status", isArray: true },
      user: { label: "User", isArray: true },
      warehouse: { label: "Warehouse", isArray: true },
      instance: { label: "Instance", isArray: true },
    });
  }, [registerFilters]);

  // Skip the main opportunities + filter-names fetches when the Priority tab
  // is active — neither feeds the priority list.
  const { data: opportunitiesData, isLoading: isOpportunitiesDataLoading } = useOpportunitiesQuery({
    enabled: needsOpportunitiesData,
  });
  const { isLoading: isOpportunitiesFiltersLoading } = useOpportunityNamesQuery({
    enabled: needsOpportunitiesData,
  });

  const userList = useMemo(() => {
    return userListWithUnassigned.map(user => ({
      label: user.label,
      value: user.value,
    }));
  }, [userListWithUnassigned]);

  // Keep the page chrome (header bar, filter tags, summary, tab strip)
  // mounted at all times and let only the All-tab table body show the
  // loader. Previously a full-page loader replaced everything while the
  // opportunities data loaded, which made the Saved Filters / date /
  // Columns bar flicker (hide then re-show) on tab switches.
  const isOpportunitiesLoading =
    needsOpportunitiesData &&
    (!opportunitiesData || isOpportunitiesDataLoading || isOpportunitiesFiltersLoading);

  return (
    <div className={styles.explorer}>
      <ExplorerHeader />
      {/* Filter tags + summary render on every tab now. The Priority tab
          honours the same Filter By / date-range controls as the main list
          (Groups-style), so the active-filter chips are meaningful there
          too. The summary cards show tenant-wide savings totals for the
          date range — a useful page-level header on every tab. */}
      <ExplorerFilterTags userList={userList} />
      <ExplorerSummary />
      <ExplorerTabs data={opportunitiesData} isLoading={isOpportunitiesLoading} />
    </div>
  );
};
